import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ArrowLeft,
  GripVertical,
  ChevronUp,
  ChevronDown,
  Plus,
  X,
  Play,
  Printer,
  FileDown,
  Music as MusicIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import jsPDF from "jspdf";
import ShareButton from "@/components/ShareButton";
import {
  fetchSetlist,
  fetchSongs,
  addSongToSetlist,
  removeSongFromSetlist,
  reorderSetlist,
} from "@/lib/api";
import { formatDuration, formatTotal } from "@/lib/format";

export default function SetlistDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [setlist, setSetlist] = useState(null);
  const [allSongs, setAllSongs] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");

  const load = () => {
    fetchSetlist(id).then(setSetlist).catch(() => navigate("/setlists"));
    fetchSongs().then(setAllSongs);
  };

  useEffect(() => {
    load();
  }, [id]);

  const songMap = useMemo(
    () => Object.fromEntries(allSongs.map((s) => [s.id, s])),
    [allSongs]
  );
  const orderedSongs = useMemo(
    () => (setlist?.song_ids || []).map((sid) => songMap[sid]).filter(Boolean),
    [setlist, songMap]
  );
  const totalSec = orderedSongs.reduce(
    (acc, s) => acc + (s.duration_seconds || 0),
    0
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = setlist.song_ids.indexOf(active.id);
    const newIndex = setlist.song_ids.indexOf(over.id);
    const newOrder = arrayMove(setlist.song_ids, oldIndex, newIndex);
    setSetlist({ ...setlist, song_ids: newOrder });
    try {
      await reorderSetlist(id, newOrder);
    } catch {
      toast.error("Erro ao reordenar");
      load();
    }
  };

  const move = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= setlist.song_ids.length) return;
    const newOrder = arrayMove(setlist.song_ids, index, target);
    setSetlist({ ...setlist, song_ids: newOrder });
    try {
      await reorderSetlist(id, newOrder);
    } catch {
      toast.error("Erro ao mover");
      load();
    }
  };

  const handleAdd = async (songId) => {
    try {
      const updated = await addSongToSetlist(id, songId);
      setSetlist(updated);
      toast.success("Música adicionada");
    } catch {
      toast.error("Erro ao adicionar música");
    }
  };

  const handleRemove = async (songId) => {
    try {
      const updated = await removeSongFromSetlist(id, songId);
      setSetlist(updated);
      toast.success("Música removida do setlist");
    } catch {
      toast.error("Erro ao remover");
    }
  };

  const handleExportPDF = () => {
    if (!setlist || orderedSongs.length === 0) {
      toast.error("Adicione músicas antes de exportar");
      return;
    }
    const doc = new jsPDF();
    let y = 20;
    doc.setFontSize(18);
    doc.text(setlist.name, 14, y);
    y += 8;
    doc.setFontSize(11);
    doc.setTextColor(100);
    if (setlist.venue) {
      doc.text(setlist.venue, 14, y);
      y += 6;
    }
    if (setlist.show_date) {
      doc.text(setlist.show_date, 14, y);
      y += 6;
    }
    doc.text(`Tempo total estimado: ${formatTotal(totalSec)}`, 14, y);
    y += 10;
    doc.setDrawColor(180);
    doc.line(14, y, 196, y);
    y += 8;
    doc.setTextColor(0);
    doc.setFontSize(12);
    orderedSongs.forEach((song, idx) => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      doc.setFont(undefined, "bold");
      doc.text(`${String(idx + 1).padStart(2, "0")}. ${song.title}`, 14, y);
      y += 6;
      doc.setFont(undefined, "normal");
      doc.setTextColor(80);
      doc.setFontSize(10);
      const meta = `${song.artist} · ${song.key || "—"} · ${song.bpm || "—"} BPM · ${formatDuration(song.duration_seconds)}`;
      doc.text(meta, 14, y);
      y += 5;
      if (song.equipment) {
        const lines = doc.splitTextToSize(`Equip.: ${song.equipment}`, 180);
        doc.text(lines, 14, y);
        y += lines.length * 5;
      }
      y += 4;
      doc.setFontSize(12);
      doc.setTextColor(0);
    });
    doc.save(`${setlist.name.replace(/\s+/g, "_")}.pdf`);
    toast.success("PDF exportado");
  };

  const availableSongs = allSongs.filter(
    (s) =>
      !setlist?.song_ids.includes(s.id) &&
      (s.title.toLowerCase().includes(pickerQuery.toLowerCase()) ||
        s.artist.toLowerCase().includes(pickerQuery.toLowerCase()))
  );

  if (!setlist) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 text-center text-zinc-500">
        Carregando...
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12 pb-32">
      <Link
        to="/setlists"
        className="inline-flex items-center text-sm text-zinc-400 hover:text-[#00F0FF] mb-5 transition-colors"
        data-testid="back-to-setlists"
      >
        <ArrowLeft className="w-4 h-4 mr-1.5" /> Voltar aos setlists
      </Link>

      <div className="mb-8">
        <p className="font-mono text-xs uppercase tracking-[0.24em] text-[#00F0FF] mb-2">
          // setlist ativo
        </p>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2" data-testid="setlist-title">
          {setlist.name}
        </h1>
        <div className="text-zinc-500 text-sm space-y-0.5">
          {setlist.venue && <p>{setlist.venue}</p>}
          {setlist.show_date && <p className="font-mono">{setlist.show_date}</p>}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#00F0FF] hover:bg-[#33F3FF] text-black font-semibold"
              data-testid="add-song-to-setlist-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Adicionar música
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#1A1A1A] border-[#2D2D2D] text-white max-w-xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-xl">Adicionar do banco</DialogTitle>
            </DialogHeader>
            <Input
              value={pickerQuery}
              onChange={(e) => setPickerQuery(e.target.value)}
              placeholder="Buscar música..."
              className="bg-[#121212] border-[#2D2D2D] text-white placeholder:text-zinc-600 focus-visible:ring-[#00F0FF]"
              data-testid="picker-search-input"
            />
            <div className="overflow-y-auto flex-1 -mx-1 px-1 space-y-1.5">
              {availableSongs.length === 0 ? (
                <p className="text-center text-zinc-500 py-8 text-sm">
                  {allSongs.length === 0
                    ? "Cadastre músicas no banco primeiro."
                    : "Todas as músicas filtradas já estão no setlist."}
                </p>
              ) : (
                availableSongs.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => handleAdd(s.id)}
                    data-testid={`picker-add-${s.id}`}
                    className="w-full text-left p-3 rounded-md border border-[#2D2D2D] hover:border-[#00F0FF] hover:bg-[#222] transition-all duration-150"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium truncate">{s.title}</p>
                        <p className="text-xs text-zinc-500 truncate">
                          {s.artist}
                        </p>
                      </div>
                      <div className="font-mono text-xs text-[#00F0FF] flex-shrink-0">
                        {formatDuration(s.duration_seconds)}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

        <Button
          onClick={handleExportPDF}
          variant="outline"
          className="bg-transparent border-[#2D2D2D] text-white hover:bg-[#1A1A1A]"
          data-testid="export-pdf-btn"
        >
          <FileDown className="w-4 h-4 mr-2" /> Exportar PDF
        </Button>

        <Button
          onClick={() => window.print()}
          variant="outline"
          className="bg-transparent border-[#2D2D2D] text-white hover:bg-[#1A1A1A] no-print"
          data-testid="print-btn"
        >
          <Printer className="w-4 h-4 mr-2" /> Imprimir
        </Button>

        <ShareButton setlist={setlist} onUpdate={setSetlist} />

        <Link to={`/performance/${id}`} className="ml-auto">
          <Button
            className="bg-[#39FF14] hover:bg-[#5cff43] text-black font-semibold"
            data-testid="enter-performance-btn"
          >
            <Play className="w-4 h-4 mr-2 fill-current" /> Modo Performance
          </Button>
        </Link>
      </div>

      {/* Songs list */}
      {orderedSongs.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[#2D2D2D] bg-[#1A1A1A]/60 p-12 text-center">
          <MusicIcon className="w-10 h-10 mx-auto text-zinc-700 mb-3" />
          <p className="text-lg font-semibold mb-1">Setlist vazio</p>
          <p className="text-zinc-500 text-sm">
            Adicione músicas do seu banco para começar a montar o show.
          </p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={setlist.song_ids}
            strategy={verticalListSortingStrategy}
          >
            <ul className="space-y-2" data-testid="setlist-songs">
              {orderedSongs.map((song, idx) => (
                <SortableSongRow
                  key={song.id}
                  song={song}
                  index={idx}
                  total={orderedSongs.length}
                  onMoveUp={() => move(idx, -1)}
                  onMoveDown={() => move(idx, 1)}
                  onRemove={() => handleRemove(song.id)}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      {/* Sticky total */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40 border-t border-[#2D2D2D] bg-[#121212]/95 backdrop-blur no-print"
        data-testid="total-time-bar"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
              Tempo total estimado
            </span>
            <span className="font-mono text-xl text-[#00F0FF]" data-testid="total-time-value">
              {formatTotal(totalSec)}
            </span>
          </div>
          <div className="font-mono text-sm text-zinc-400">
            {orderedSongs.length} faixa{orderedSongs.length === 1 ? "" : "s"}
          </div>
        </div>
      </div>
    </div>
  );
}

function SortableSongRow({ song, index, total, onMoveUp, onMoveDown, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: song.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      data-testid={`setlist-row-${song.id}`}
      className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-lg border border-[#2D2D2D] bg-[#1A1A1A] hover:border-[#00F0FF]/40 transition-colors"
    >
      <button
        {...attributes}
        {...listeners}
        className="text-zinc-500 hover:text-[#00F0FF] cursor-grab active:cursor-grabbing touch-none p-1"
        data-testid={`drag-handle-${song.id}`}
        aria-label="Arrastar"
      >
        <GripVertical className="w-5 h-5" />
      </button>

      <div className="font-mono text-xs text-zinc-500 w-7 text-center flex-shrink-0">
        {String(index + 1).padStart(2, "0")}
      </div>

      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{song.title}</p>
        <p className="text-xs text-zinc-500 truncate">{song.artist}</p>
      </div>

      <div className="hidden sm:flex flex-col items-end font-mono text-xs flex-shrink-0">
        <span className="text-[#00F0FF]">{song.key || "—"}</span>
        <span className="text-zinc-500">
          {song.bpm || "—"} BPM · {formatDuration(song.duration_seconds)}
        </span>
      </div>

      <div className="sm:hidden font-mono text-xs text-[#00F0FF] flex-shrink-0">
        {formatDuration(song.duration_seconds)}
      </div>

      <div className="flex flex-col">
        <Button
          variant="ghost"
          size="sm"
          onClick={onMoveUp}
          disabled={index === 0}
          data-testid={`move-up-${song.id}`}
          className="h-6 w-6 p-0 text-zinc-400 hover:text-[#00F0FF] hover:bg-[#2D2D2D] disabled:opacity-30"
        >
          <ChevronUp className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onMoveDown}
          disabled={index === total - 1}
          data-testid={`move-down-${song.id}`}
          className="h-6 w-6 p-0 text-zinc-400 hover:text-[#00F0FF] hover:bg-[#2D2D2D] disabled:opacity-30"
        >
          <ChevronDown className="w-4 h-4" />
        </Button>
      </div>

      <Button
        variant="ghost"
        size="sm"
        onClick={onRemove}
        data-testid={`remove-from-setlist-${song.id}`}
        className="h-8 w-8 p-0 text-zinc-500 hover:text-red-400 hover:bg-[#2D2D2D]"
      >
        <X className="w-4 h-4" />
      </Button>
    </li>
  );
}
